<template>
  <div class="sam-navigation">
    <div class="sam-navigation__fixed">
      <router-link
        to="/home/dashboard"
        class="sam-navigation__logo"
        title="Go to dashboard"
        style="height: 47px "
      >
        <img :src="companyLogo" alt="" style="max-width: 220px; max-height: 40px;" />
      </router-link>
      <v-list-item @click="goTo('/home/dashboard')" class="text-center bg-primary">
        Customer Profile &amp; SAM
      </v-list-item>
      <div v-if="canViewAllCustomers" class="sam-navigation__employee">
        <v-autocomplete
          :model-value="selectedEmployeeId"
          :items="employeeOptions"
          :item-title="item => `${item.code}-${item.name}` ?? 'N/A'"
          item-value="id"
          label="Filter by account owner"
          prepend-inner-icon="mdi-account-filter-outline"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          @update:model-value="selectEmployee"
        
            :menu-props="{ contentClass: 'my-custom-dropdown' }"
/>
      </div>
      <div class="sam-navigation__search">
        <v-text-field
          v-model="search"
          placeholder="Search Strategic Account"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          clearable
        />
      </div>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" />

    <v-list nav density="compact" class="sam-navigation__list">
      <v-list-item
        v-for="customer in filteredCustomers"
        :key="customer.id"
        :active="selectedCustomerId === Number(customer.id)"
        class="sam-account"
        @click="selectCustomer(customer)"
      >
        <v-list-item-title>
          {{ customer.company_name }}
        </v-list-item-title>

        <v-list-item-subtitle>
          {{ customer.industry || "STRATEGIC ACCOUNT" }}
        </v-list-item-subtitle>
        <template #append
          ><span class="account-dot" :class="dotClass(customer)"
        /></template>
      </v-list-item>

      <div
        v-if="!loading && !filteredCustomers.length"
        class="pa-5 text-center text-medium-emphasis"
      >
        No assigned SAM customer found.
      </div>
    </v-list>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import Axios from "axios";
import { customerProfileAccountsUrl } from "@/store/module/customerProfile/api";
import fallbackCompanyLogo from "@/assets/image/logo.png";

const route = useRoute();
const router = useRouter();
const store = useStore();

const customers = ref([]);
const employees = ref([]);
const canViewAllCustomers = ref(false);
const search = ref("");
const loading = ref(false);
const companyLogo = computed(() => {
  const company = store.getters.getCurrentCompany;
  return company?.logo_thumb
    ? store.getters.getFileServerUrl + company.logo_thumb
    : fallbackCompanyLogo;
});

const goTo = (val)=> {
  router.push({ path: val }).catch(err => {});
};
const selectedCustomerId = computed(() => Number(route.query.customer_id || 0));
const currentBranchId = computed(() =>
  Number(store.getters.getCurrentBranch?.id || 0),
);
const selectedEmployeeId = computed(() => Number(route.query.employee_id || 0));
const employeeOptions = computed(() => [
  { id: 0, code: 'All', name: "Employees" },
  ...employees.value,
]);

const filteredCustomers = computed(() => {
  const keyword = search.value.trim().toLowerCase();

  if (!keyword) {
    return customers.value;
  }

  return customers.value.filter((customer) =>
    `${customer.company_name} ${customer.code}`.toLowerCase().includes(keyword),
  );
});

const dotClass = (customer) => {
  const priority = String(
    customer.customer_profile?.account_priority || "",
  ).toLowerCase();
  if (priority === "high") return "account-dot--red";
  if (priority === "medium") return "account-dot--amber";
  return "account-dot--green";
};

async function loadCustomers() {
  loading.value = true;

  try {
    const response = await Axios.get(customerProfileAccountsUrl, {
      params: {
        per_page: 100,
        sam_only: 1,
        employee_id: selectedEmployeeId.value || undefined,
      },
    });

    customers.value = response.data?.data || [];
    canViewAllCustomers.value = Boolean(
      response.data?.filters?.can_view_all_customers,
    );
    employees.value = response.data?.filters?.employees || [];
  } finally {
    loading.value = false;
  }
}

function selectEmployee(employeeId) {
  router.replace({
    path: "customer-profile",
    query: {
      ...route.query,
      employee_id: Number(employeeId) || undefined,
      customer_id: undefined,
    },
  });
}

function selectCustomer(customer) {
  router.push({
    path: "customer-profile",
    query: {
      ...route.query,
      customer_id: customer.id,
      tab: route.query.tab || "overview",
    },
  });
}

// onMounted(loadCustomers);
// watch(selectedEmployeeId, loadCustomers);
onMounted(loadCustomers);

watch(selectedEmployeeId, loadCustomers);

watch(currentBranchId, async (newBranchId, oldBranchId) => {
  if (!oldBranchId || newBranchId === oldBranchId) {
    return;
  }

  // আগের branch-এর employee/customer selection সরিয়ে ফেলুন।
  customers.value = [];
  employees.value = [];
  search.value = "";

  await router.replace({
    path: "customer-profile",
    query: {
      ...route.query,
      employee_id: undefined,
      customer_id: undefined,
    },
  });

  await loadCustomers();
});
</script>

<style scoped>
.sam-navigation {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  color: #111827;
}
.sam-navigation__fixed {
  position: sticky;
  top: 0;
  z-index: 5;
  flex: 0 0 auto;
  background: #fff;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.1);
}
.sam-navigation__logo {
  height: 74px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 9px 18px;
  background: #fff;
}
.sam-navigation__logo img {
  max-width: 210px;
  max-height: 56px;
  object-fit: contain;
}
.sam-navigation__title {
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #176fc1;
  color: #fff;
  font-size: 19px;
  font-weight: 600;
}
.sam-navigation__employee {
  padding: 14px 14px 0;
  background: #fff;
}
.sam-navigation__search {
  padding: 10px 14px 14px;
  background: #fff;
}
.sam-navigation__employee :deep(.v-field),
.sam-navigation__search :deep(.v-field) {
  background: #fff;
  border-radius: 5px;
}
.sam-navigation__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 8px 18px;
  background: #fff;
  color: #111827;
}
.sam-account {
  min-height: 68px;
  padding: 10px 12px;
  border-radius: 7px !important;
}
.sam-account:hover,
.sam-account.v-list-item--active {
  background: #e3edf9 !important;
  color: #0b62b3 !important;
}

:deep(.v-list-item-title) {
  font-size: 15px;
  font-weight: 700;
  white-space: normal;
}
:deep(.v-list-item-subtitle) {
  color: #6b7280 !important;
  font-size: 11px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-top: 4px;
}
.account-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: block;
  margin-left: 10px;
}
.account-dot--green {
  background: #5c9870;
}
.account-dot--amber {
  background: #d18b1d;
}
.account-dot--red {
  background: #c85643;
}
</style>
